export type FilterLifecycle = 'idle' | 'loading-metadata' | 'ready' | 'applying';

export interface FilterFacetCounts {
  total: number;
  eventTypes: Record<string, number>;
  organizations: Record<string, number>;
  educationalPaths: Record<string, number>;
  modules: Record<string, number>;
  locations: Record<string, number>;
  hiddenCounts?: {
    subEvents: number;
    fullEvents: number;
    participated: number;
  };
}

export function createEmptyFacetCounts(): FilterFacetCounts {
  return {
    total: 0,
    eventTypes: {},
    organizations: {},
    educationalPaths: {},
    modules: {},
    locations: {},
    hiddenCounts: { subEvents: 0, fullEvents: 0, participated: 0 },
  };
}

export interface FilterOrganization {
  id: number;
  org_name: string;
  full_name?: string;
  groupCalendarEnabled?: boolean;
}

export interface PathVersionOption {
  version_id: number;
  path_id: number;
  display_name?: string;
  name?: string;
  start_date?: string;
  end_date?: string;
}

export interface FilterEducationalPath {
  path_id: number;
  name: string;
  full_name: string;
  guid: string;
  versionCount: number;
  /** Organizations that have at least one user assigned to this path. */
  organizationIds?: number[];
  /** Present on filter-metadata so clients need not call privileged /paths/:uuid/versions. */
  versions?: PathVersionOption[];
}

export interface FilterModule {
  module_id: number;
  name: string;
}

export interface FilterLocation {
  guid: string;
  locations_name: string;
}

export interface FilterVisibility {
  showExercises: boolean;
  showEducationalPaths: boolean;
  showModules: boolean;
  showLocations: boolean;
  showDistance: boolean;
  showHideFull: boolean;
  showHideParticipated: boolean;
  showGroupCalendarEvents?: boolean;
  superadminGroupCalendarFilter?: boolean;
}

export interface FilterMetadata {
  organizations: FilterOrganization[];
  defaultOrganizationIds: number[];
  educationalPaths: FilterEducationalPath[];
  defaultEducationalPathIds: number[];
  modules: FilterModule[];
  locations: FilterLocation[];
  defaultLocationGuids: string[];
  defaults: {
    eventTypes: string[];
    hideSubEvents: boolean;
  };
  visibility: FilterVisibility;
}

export interface DistanceFilter {
  enabled: boolean;
  latitude: number | null;
  longitude: number | null;
  radiusKm: number | null;
}

export interface EventsFilterCriteria {
  eventType: string[];
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  visibility: string[];
  searchText: string;
  organizationIds: number[];
  hideSubEvents: boolean;
  educationalPathIds: number[];
  educationalPathVersionIds: number[];
  moduleIds: number[];
  locationGuids: string[];
  hideFullEvents: boolean;
  hideParticipatedEvents: boolean;
  showGroupCalendarEvents: boolean;
  distanceFilter: DistanceFilter;
}

export interface EventQueryParams {
  eventTypes?: string[];
  search?: string;
  organizationIds?: number[];
  organization_ids?: string;
  educational_path_ids?: string;
  educational_path_version_ids?: string;
  module_ids?: string;
  location_guids?: string;
  hide_full?: string;
  exclude_participated?: string;
  /** Client-only — triggers reload when toggled; not sent to API. */
  hideSubEvents?: boolean;
  lat?: string;
  lng?: string;
  radius_km?: string;
  startDate?: string;
  endDate?: string;
  exclude_group_events?: string;
}

export function createEmptyCriteria(): EventsFilterCriteria {
  return {
    eventType: [],
    dateRange: { start: null, end: null },
    visibility: [],
    searchText: '',
    organizationIds: [],
    hideSubEvents: true,
    educationalPathIds: [],
    educationalPathVersionIds: [],
    moduleIds: [],
    locationGuids: [],
    hideFullEvents: false,
    hideParticipatedEvents: false,
    showGroupCalendarEvents: false,
    distanceFilter: {
      enabled: false,
      latitude: null,
      longitude: null,
      radiusKm: null,
    },
  };
}

export function resolveOrganizationIdsForGroupCalendarFilter(
  metadata: FilterMetadata,
  criteria: EventsFilterCriteria
): number[] {
  const ids = criteria.organizationIds?.length
    ? criteria.organizationIds
    : metadata.defaultOrganizationIds || [];
  return ids.map((id) => Number(id)).filter((id) => !isNaN(id) && id > 0);
}

export function selectedOrgsHaveGroupCalendarEnabled(
  metadata: FilterMetadata,
  orgIds: number[]
): boolean {
  if (!orgIds.length) {
    return false;
  }
  const orgs = metadata.organizations ?? [];
  return orgIds.some((id) =>
    orgs.some((o) => o.id === id && o.groupCalendarEnabled === true)
  );
}

/** Whether the group-calendar events toggle may be shown in the filter UI. */
export function canShowGroupCalendarEventsFilter(
  metadata: FilterMetadata | null,
  criteria: EventsFilterCriteria
): boolean {
  if (!metadata?.visibility) {
    return false;
  }
  const v = metadata.visibility;
  if (v.showGroupCalendarEvents) {
    return true;
  }
  if (v.superadminGroupCalendarFilter) {
    const orgIds = resolveOrganizationIdsForGroupCalendarFilter(metadata, criteria);
    return selectedOrgsHaveGroupCalendarEnabled(metadata, orgIds);
  }
  return false;
}

export function buildQueryParamsFromCriteria(criteria: EventsFilterCriteria): EventQueryParams {
  const params: EventQueryParams = {
    exclude_group_events: criteria.showGroupCalendarEvents ? 'false' : 'true',
  };

  if (criteria.eventType?.length) {
    params.eventTypes = [...criteria.eventType];
  }
  if (criteria.searchText?.trim()) {
    params.search = criteria.searchText.trim();
  }
  if (criteria.organizationIds?.length) {
    params.organizationIds = [...criteria.organizationIds];
    params.organization_ids = criteria.organizationIds.join(',');
  }
  if (criteria.educationalPathVersionIds?.length) {
    params.educational_path_version_ids = criteria.educationalPathVersionIds.join(',');
  } else if (criteria.educationalPathIds?.length) {
    params.educational_path_ids = criteria.educationalPathIds.join(',');
  }
  if (criteria.moduleIds?.length) {
    params.module_ids = criteria.moduleIds.join(',');
  }
  if (criteria.locationGuids?.length) {
    params.location_guids = criteria.locationGuids.join(',');
  }
  if (criteria.hideFullEvents) {
    params.hide_full = 'true';
  }
  if (criteria.hideParticipatedEvents) {
    params.exclude_participated = 'true';
  }
  params.hideSubEvents = criteria.hideSubEvents === true;
  const df = criteria.distanceFilter;
  if (df?.enabled && df.latitude != null && df.longitude != null && df.radiusKm != null && df.radiusKm > 0) {
    params.lat = String(df.latitude);
    params.lng = String(df.longitude);
    params.radius_km = String(df.radiusKm);
  }

  return params;
}

/** Maps filter criteria to API query string keys used by /events and /events/filter-facets. */
export function buildHttpQueryFromEventParams(params: EventQueryParams): Record<string, string> {
  const http: Record<string, string> = {};
  if (params.exclude_group_events === 'true') {
    http['exclude_group_events'] = 'true';
  }

  if (params.eventTypes?.length) {
    http['event_types'] = params.eventTypes.join(',');
  }
  if (params.search) {
    http['search'] = params.search;
  }
  if (params.organization_ids) {
    http['organization_ids'] = params.organization_ids;
  } else if (params.organizationIds?.length) {
    http['organization_ids'] = params.organizationIds.join(',');
  }
  if (params.educational_path_version_ids) {
    http['educational_path_version_ids'] = params.educational_path_version_ids;
  } else if (params.educational_path_ids) {
    http['educational_path_ids'] = params.educational_path_ids;
  }
  if (params.module_ids) {
    http['module_ids'] = params.module_ids;
  }
  if (params.location_guids) {
    http['location_guids'] = params.location_guids;
  }
  if (params.hide_full) {
    http['hide_full'] = params.hide_full;
  }
  if (params.exclude_participated) {
    http['exclude_participated'] = params.exclude_participated;
  }
  if (params.lat) {
    http['lat'] = params.lat;
  }
  if (params.lng) {
    http['lng'] = params.lng;
  }
  if (params.radius_km) {
    http['radius_km'] = params.radius_km;
  }
  if (params.startDate) {
    http['startDate'] = params.startDate;
  }
  if (params.endDate) {
    http['endDate'] = params.endDate;
  }
  if (params.hideSubEvents) {
    http['exclude_sub_events'] = 'true';
  }

  return http;
}

/** Maps raw /events load query params to facet API query string (same keys as HTTP GET). */
export function buildHttpQueryFromLoadParams(queryParams: Record<string, unknown>): Record<string, string> {
  const http: Record<string, string> = {};
  const excludeGroup = queryParams['exclude_group_events'];
  if (excludeGroup === undefined || excludeGroup === null || excludeGroup === '') {
    http['exclude_group_events'] = 'true';
  } else if (String(excludeGroup) === 'true' || String(excludeGroup) === '1') {
    http['exclude_group_events'] = 'true';
  }
  const keys = [
    'exclude_group_events',
    'organization_ids',
    'event_types',
    'educational_path_ids',
    'educational_path_version_ids',
    'module_ids',
    'location_guids',
    'hide_full',
    'exclude_participated',
    'exclude_sub_events',
    'lat',
    'lng',
    'radius_km',
    'search',
    'startDate',
    'endDate',
    'catering',
  ];
  for (const key of keys) {
    const val = queryParams[key];
    if (val !== undefined && val !== null && val !== '') {
      http[key] = String(val);
    }
  }
  return http;
}
